/**
 * 외부 패스워드 매니저 CSV/JSON Import 파서
 *
 * 컬럼명 기반 자동 매핑 + 사용자 오버라이드 방식
 * 지원 포맷: CSV, JSON (Bitwarden)
 */

// ---------- 컬럼명 → Safebox 필드 타입 매핑 ----------

/**
 * 정확한 컬럼명 → Safebox 필드 타입 매핑 (우선 적용)
 */
const EXACT_COLUMN_MAP = {
  name: 'title',
  Title: 'title',
  title: 'title',
  url: 'url',
  Url: 'url',
  URL: 'url',
  login_uri: 'url',
  Website: 'url',
  username: 'string',
  Username: 'string',
  login_username: 'string',
  password: 'password',
  Password: 'password',
  login_password: 'password',
  OTPAuth: 'totp',
  totp: 'totp',
  login_totp: 'totp',
  otp: 'totp',
  note: 'note',
  Notes: 'note',
  notes: 'note',
  extra: 'note',
}

/**
 * 키워드 포함 매칭 (정확 매칭 실패 시 사용)
 * 컬럼명을 소문자로 변환 후 키워드 포함 여부로 판단
 * 우선순위 순서: 먼저 매칭되는 것이 적용됨
 */
const KEYWORD_PATTERNS = [
  // URL 계열
  {
    keywords: ['url', 'uri', 'website', 'web_site', 'homepage', 'link', 'site_url', 'webpage'],
    type: 'url',
  },
  // Password 계열 (username보다 먼저 — "pass"가 "user"보다 구체적)
  {
    keywords: ['password', 'passwd', 'pass', 'secret', 'credential', 'pw', 'pin'],
    type: 'password',
  },
  // TOTP 계열
  { keywords: ['otp', 'totp', '2fa', 'mfa', 'authenticator'], type: 'totp' },
  // Note/Memo 계열
  { keywords: ['note', 'memo', 'comment', 'description', 'remark', 'extra'], type: 'note' },
  // Email 계열
  { keywords: ['email', 'e-mail', 'mail_addr', 'mail_address'], type: 'email' },
  // Username 계열
  { keywords: ['user', 'login_id', 'login_name', 'account', 'member'], type: 'string' },
  // Title 계열
  { keywords: ['name', 'title', 'label', 'service', 'site_name'], type: 'title' },
]

/**
 * 컬럼명으로 Safebox 필드 타입을 추론
 * 1순위: 정확한 컬럼명 매칭
 * 2순위: 키워드 포함 매칭 (소문자 변환 후)
 * 3순위: 'skip'
 */
export const inferColumnType = (columnName) => {
  // 1순위: 정확 매칭
  if (EXACT_COLUMN_MAP[columnName]) {
    return EXACT_COLUMN_MAP[columnName]
  }

  // 2순위: 키워드 포함 매칭
  const lower = columnName.toLowerCase()
  const matched = KEYWORD_PATTERNS.find((pattern) =>
    pattern.keywords.some((kw) => lower.includes(kw)),
  )
  if (matched) return matched.type

  // 3순위: 매칭 실패
  return 'skip'
}

// 하위 호환: 기존 테스트에서 참조하는 COLUMN_TYPE_MAP 유지
export const COLUMN_TYPE_MAP = EXACT_COLUMN_MAP

/**
 * Safebox 필드 타입 정의 (드롭다운 선택지)
 */
export const FIELD_TYPES = [
  { value: 'url', label: 'URL', encrypt: false, custom: true },
  { value: 'string', label: '문자열', encrypt: false, custom: true },
  { value: 'email', label: '이메일', encrypt: false, custom: true },
  { value: 'password', label: '비밀번호', encrypt: true, custom: true },
  { value: 'totp', label: 'TOTP', encrypt: true, custom: true },
  { value: 'note', label: '메모', encrypt: false, custom: false },
  { value: 'skip', label: '건너뛰기', encrypt: false, custom: false },
]

// ---------- CSV 파싱 (유지) ----------

/**
 * CSV 텍스트를 행 배열로 파싱 (따옴표 내 쉼표/줄바꿈 처리)
 */
const parseCsvRows = (text) => {
  const rows = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i]

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '""'
          i += 1
        } else {
          inQuotes = false
          current += char
        }
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
      current += char
    } else if (char === '\n' || (char === '\r' && text[i + 1] === '\n')) {
      rows.push(current)
      current = ''
      if (char === '\r') i += 1
    } else if (char === '\r') {
      rows.push(current)
      current = ''
    } else {
      current += char
    }
  }

  if (current.length > 0) {
    rows.push(current)
  }

  return rows
}

/**
 * CSV 행 문자열을 필드 배열로 분리 (따옴표 처리 포함)
 */
const splitCsvLine = (line) => {
  const fields = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      fields.push(current)
      current = ''
    } else {
      current += char
    }
  }

  fields.push(current)
  return fields
}

// ---------- 공통 유틸 (유지) ----------

/**
 * URL에서 도메인 추출 (title fallback)
 */
export const extractDomain = (url) => {
  if (!url) return ''
  try {
    const parsed = new URL(url)
    return parsed.hostname
  } catch {
    const match = url.match(/(?:https?:\/\/)?([^/]+)/)
    return match ? match[1] : url
  }
}

/**
 * JSON 데이터가 Safebox 백업 형식인지 확인
 */
export const isSafeboxBackup = (data) => {
  if (!data || typeof data !== 'object') return false
  if (data.encrypted === true) return true
  if (data.version && Array.isArray(data.records)) return true
  if (data.title && Array.isArray(data.items)) return true
  return false
}

// ---------- 신규: 컬럼 기반 매핑 ----------

/**
 * CSV 텍스트를 헤더 + 행 객체 배열로 파싱 (매핑 모달용)
 * @param {string} csvText
 * @returns {{ headers: string[], rows: Object[] }}
 */
export const parseCsvRaw = (csvText) => {
  const rawRows = parseCsvRows(csvText.trim())
  if (rawRows.length < 1) {
    return { headers: [], rows: [] }
  }

  const headers = splitCsvLine(rawRows[0]).map((h) => h.trim())
  const rows = []

  rawRows.slice(1).forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line) return

    const fields = splitCsvLine(line)
    const rowObj = {}
    headers.forEach((header, idx) => {
      rowObj[header] = fields[idx] || ''
    })
    rows.push(rowObj)
  })

  return { headers, rows }
}

/**
 * 헤더 배열로 자동 매핑 생성
 * COLUMN_TYPE_MAP에 있는 컬럼 → 해당 타입, 없는 컬럼 → 'skip'
 * @param {string[]} headers
 * @returns {Array<{column: string, type: string}>}
 */
export const buildAutoMapping = (headers) => {
  return headers.map((column) => ({
    column,
    type: inferColumnType(column),
  }))
}

/**
 * 행 배열을 최종 매핑으로 Safebox import records로 변환
 * title만 있으면 리소스 생성 (아이템 0개 OK)
 * 빈 값의 필드는 아이템으로 추가하지 않음
 * @param {Object[]} rows
 * @param {Array<{column: string, type: string}>} mapping
 * @returns {{ records: Array, skippedRows: Array }}
 */
export const convertWithMapping = (rows, mapping) => {
  const records = []
  const skippedRows = []

  // title 컬럼 찾기
  const titleMapping = mapping.find((m) => m.type === 'title')
  const titleColumn = titleMapping ? titleMapping.column : null

  // title 외 활성 매핑 (skip 제외)
  const activeMapping = mapping.filter((m) => m.type !== 'title' && m.type !== 'skip')

  const fieldTypeConfig = {}
  FIELD_TYPES.forEach((ft) => {
    fieldTypeConfig[ft.value] = ft
  })

  rows.forEach((row, idx) => {
    try {
      // Title 결정
      let title = titleColumn ? (row[titleColumn] || '').trim() : ''

      // title fallback: URL 도메인 → "Untitled"
      if (!title) {
        const urlMapping = activeMapping.find((m) => m.type === 'url')
        const urlVal = urlMapping ? (row[urlMapping.column] || '').trim() : ''
        title = extractDomain(urlVal) || 'Untitled'
      }

      // 아이템 생성
      const items = []

      activeMapping.forEach((m) => {
        const value = (row[m.column] || '').trim()
        if (!value) return

        const config = fieldTypeConfig[m.type]
        if (!config) return

        items.push({
          // PR10: V2 매핑이 totp 를 정식 지원 — 본 레거시 경로(importV2 OFF 전용)는
          // totp 타입 도입 전의 다운그레이드를 의도적으로 유지(레거시 무수정 원칙)
          type: m.type === 'totp' ? 'password' : m.type,
          label: m.type === 'totp' ? 'TOTP Secret' : m.column,
          content: value,
          encrypt: config.encrypt,
          custom: config.custom,
          order: items.length + 1,
        })
      })

      // title만 있으면 리소스 생성 (아이템 0개 OK)
      records.push({ title, items })
    } catch {
      skippedRows.push({
        row: idx + 1,
        title: '',
        reason: 'parse_error',
      })
    }
  })

  return { records, skippedRows }
}

/**
 * JSON에서 객체 배열을 찾아 플랫 행으로 변환
 * Bitwarden은 중첩 구조를 풀고, 그 외 JSON은 첫 번째 객체 배열을 사용
 * @param {Object} jsonData
 * @returns {{ headers: string[], rows: Object[] }}
 */
export const flattenExternalJson = (jsonData) => {
  // Bitwarden JSON: items 배열 내 login 객체가 하나라도 존재
  if (
    Array.isArray(jsonData.items) &&
    jsonData.items.length > 0 &&
    jsonData.items.some((item) => item?.login !== undefined)
  ) {
    return flattenBitwardenJson(jsonData)
  }

  // 범용 JSON: 첫 번째 객체 배열을 찾아서 사용
  return flattenGenericJson(jsonData)
}

/**
 * Bitwarden JSON을 플랫 행 배열로 변환
 */
const flattenBitwardenJson = (jsonData) => {
  const headers = ['name', 'url', 'username', 'password', 'totp', 'notes']
  const rows = []

  const items = jsonData.items || []
  items.forEach((item) => {
    const login = item.login || {}
    const row = {
      name: item.name || '',
      url: login.uris?.[0]?.uri || '',
      username: login.username || '',
      password: login.password || '',
      totp: login.totp || '',
      notes: item.notes || '',
    }
    rows.push(row)
  })

  return { headers, rows }
}

/**
 * 범용 JSON: 첫 번째 객체 배열을 찾아서 헤더 + 행으로 변환
 * { "entries": [{ "label": "...", "link": "..." }] } → headers: ["label", "link"], rows: [...]
 */
const flattenGenericJson = (jsonData) => {
  // 최상위가 배열이면 그대로 사용
  const stringify = (value) => {
    if (value == null) return ''
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
  }

  if (Array.isArray(jsonData) && jsonData.length > 0 && typeof jsonData[0] === 'object') {
    const headers = Object.keys(jsonData[0])
    const rows = jsonData.map((item) => {
      const row = {}
      headers.forEach((h) => {
        row[h] = stringify(item[h])
      })
      return row
    })
    return { headers, rows }
  }

  // 객체의 값 중 첫 번째 객체 배열을 찾기
  const arrayKey = Object.keys(jsonData).find((key) => {
    const val = jsonData[key]
    return Array.isArray(val) && val.length > 0 && typeof val[0] === 'object'
  })

  if (!arrayKey) {
    throw new Error('UNSUPPORTED_JSON_FORMAT')
  }

  const items = jsonData[arrayKey]
  const headers = Object.keys(items[0])
  const rows = items.map((item) => {
    const row = {}
    headers.forEach((h) => {
      row[h] = stringify(item[h])
    })
    return row
  })

  return { headers, rows }
}

/**
 * 외부 JSON인지 판별 (Safebox 백업이 아닌 경우)
 */
export const isExternalJson = (data) => {
  if (!data || typeof data !== 'object') return false
  if (isSafeboxBackup(data)) return false
  return true
}

// ---------- Google Password Manager ----------

const GOOGLE_PASSWORD_HEADERS = new Set(['name', 'url', 'username', 'password', 'note'])

/**
 * CSV 헤더가 Google Password Manager 포맷인지 확인
 * 정확히 5개 컬럼: name, url, username, password, note (대소문자 무시)
 */
export const isGooglePasswordCsv = (headers) => {
  if (!Array.isArray(headers) || headers.length !== 5) return false
  const normalized = new Set(headers.map((h) => h.trim().toLowerCase()))
  if (normalized.size !== 5) return false
  return [...GOOGLE_PASSWORD_HEADERS].every((required) => normalized.has(required))
}

/**
 * Google Password CSV 행을 웹 로그인 템플릿 기반 import record로 변환
 * label은 웹 로그인 템플릿의 영문 라벨과 일치 (ID, Password, Website, Note)
 * order는 웹 로그인 템플릿의 ui_widgets 순서 (1=ID, 2=Password, 3=Website, 4=Note)
 */
export const convertGooglePasswordRows = (rows) => {
  const records = []
  const skippedRows = []

  rows.forEach((row, idx) => {
    try {
      const title = (row.name || '').trim() || extractDomain(row.url) || 'Untitled'

      const items = []

      // 웹 로그인 템플릿의 기본 필드에 매핑 (custom: false — 템플릿 필드로 인식)
      // 빈 값이어도 필드를 항상 생성 — 누락 시 편집 화면에서 템플릿 필드가 사라짐
      // uid=1: 아이디
      items.push({
        type: 'string',
        label: 'ID',
        content: (row.username || '').trim(),
        encrypt: false,
        custom: false,
        required: false,
        order: 1,
      })

      // uid=2: 암호
      items.push({
        type: 'password',
        label: 'Password',
        content: (row.password || '').trim(),
        encrypt: true,
        custom: false,
        required: false,
        order: 2,
      })

      // uid=3: 웹사이트 (필수)
      items.push({
        type: 'url',
        label: 'Website',
        content: (row.url || '').trim(),
        encrypt: false,
        custom: false,
        required: true,
        order: 3,
      })

      // uid=4: 첨부파일
      items.push({
        type: 'files',
        label: 'Files',
        content: [],
        encrypt: false,
        custom: false,
        required: false,
        order: 4,
      })

      // uid=-2: 메모
      items.push({
        type: 'note',
        label: 'Note',
        content: (row.note || '').trim(),
        encrypt: false,
        custom: false,
        required: false,
        order: 5,
      })

      records.push({ title, items })
    } catch {
      skippedRows.push({ row: idx + 1, title: '', reason: 'parse_error' })
    }
  })

  return { records, skippedRows }
}
